/**
 * 챗봇 타겟 선택 레이어용 통합 카탈로그 API 클라이언트.
 * 백엔드 GET /api/v9/chatbot/targets (ChatbotController.listTargets) 와 1:1.
 * (feature: chatbot-target-conversation)
 */
import { customAxiosInstance } from '@/common/api/utils/mutator';

/** 카탈로그 항목 — 백엔드 ChatbotTargetItem 와 동일. */
export interface TargetCatalogItem {
  targetId: string;
  /** 표시명 — alias 우선, 없으면 originName. */
  name: string;
  originName: string;
  /** target_kind (host/database/application/pod/...). */
  kind: string;
  /** sub_kind (oracle/mysql/...). 없으면 "". */
  subKind: string;
  /** CRITICAL | WARNING | NORMAL. */
  alarmLevel: string;
  /** 활성 알람(CRITICAL+WARNING) 카운트. */
  activeAlarms: number;
  /** 라이브 상태 — 'active' | 'inactive' | 'noSignal'. 전용 상태 소스 없는 kind 는 undefined. */
  status?: string;
}

interface TargetCatalogResponse {
  data: TargetCatalogItem[];
  total: number;
  offset: number;
  size: number;
  hasMore: boolean;
}

export interface FetchCatalogParams {
  userId: string;
  /** target_kind 필터. 미지정/"all" = 전 도메인. 콤마 구분 다중 kind(도메인 그룹) 허용. */
  category?: string;
  /** 타겟명 부분일치(대소문자 무시). */
  q?: string;
  offset?: number;
  size?: number;
  /** true = 활성(active) 타겟만 반환(서버 필터). */
  active?: boolean;
}

const TARGETS_URL = '/api/v9/chatbot/targets';

/**
 * 목록 모드 — 카테고리/검색/lazy paging. 서버가 알람·헬스 레벨로 우선 정렬.
 * 실패 시 빈 결과({ items: [], hasMore: false }) 반환(레이어가 빈 상태 표시).
 */
export async function fetchTargetCatalog(
  params: FetchCatalogParams,
): Promise<{ items: TargetCatalogItem[]; hasMore: boolean }> {
  const { userId, category, q, offset = 0, size = 50, active = false } = params;
  try {
    const res = await customAxiosInstance<TargetCatalogResponse>({
      url: TARGETS_URL,
      method: 'GET',
      params: {
        userId,
        ...(category && category !== 'all' ? { category } : {}),
        ...(q && q.trim() ? { q: q.trim() } : {}),
        ...(active ? { active: true } : {}),
        offset,
        size,
      },
    });
    return { items: res.data?.data ?? [], hasMore: !!res.data?.hasMore };
  } catch (e) {
    console.warn('[chatbot] fetchTargetCatalog failed', e);
    return { items: [], hasMore: false };
  }
}

/**
 * ids 모드 — 특정 target_id 들 상세(핀·최근 복원). 미존재 id 는 응답에서 제외됨.
 */
export async function fetchTargetsByIds(
  userId: string,
  ids: string[],
): Promise<TargetCatalogItem[]> {
  const valid = (ids || []).filter((id) => !!id);
  if (!valid.length) return [];
  try {
    const res = await customAxiosInstance<TargetCatalogResponse>({
      url: TARGETS_URL,
      method: 'GET',
      params: { userId, ids: valid.join(',') },
    });
    return res.data?.data ?? [];
  } catch (e) {
    console.warn('[chatbot] fetchTargetsByIds failed', e);
    return [];
  }
}
